<template>
  <Modal :open="open" @close="closeModal">
    <div class="text-center mb-6">
      <h2 class="text-2xl font-bold text-gray-900">Export des primes</h2>
    </div>

    <div class="space-y-6">
      <!-- Sélecteur de stores -->
      <div>
        <div class="flex items-center justify-between mb-2">
          <label class="block text-sm font-medium text-gray-700">Points de vente</label>
          <button
            v-if="filteredStores.length > 0"
            @click="selectAllStores"
            class="text-xs text-gray-500 hover:text-primary hover:underline cursor-pointer transition-all"
          >
            Tout sélectionner
          </button>
        </div>
        <div v-if="filteredStores.length > 0" class="relative">
          <div v-if="selectedStores.length > 0" class="absolute -top-2 -right-2 z-10">
            <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary text-white">
              {{ selectedStores.length }}
            </span>
          </div>
          <MultiSelectSearch
            :search-groups="searchGroups"
            placeholder="Rechercher un magasin..."
            :input-id="'export-stores-search'"
            :min-search-length="0"
            :debounce-delay="100"
            :show-component="true"
            :initial-selections="initialSelections"
            @update-selections="handleUpdateSelections"
            @search="handleSearch"
            @toggle-item="handleToggleItem"
          />
        </div>
        <p v-else class="text-sm text-gray-500">Aucun magasin disponible</p>
      </div>

      <!-- Sélecteur de périodes -->
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-2">Période</label>
        <MonthPicker v-model="selectedMonthRange" placeholder="Sélectionnez une plage de mois" />
      </div>
    </div>

    <div class="mt-8 text-center flex justify-end gap-2">
      <SecondaryButton @click="closeModal">Fermer</SecondaryButton>
      <SecondaryButton @click="handleExport" v-if="canExport">Exporter</SecondaryButton>
    </div>
  </Modal>
</template>

<script>
import { Modal, SecondaryButton, MonthPicker, MultiSelectSearch, download } from "@estaly/ui";
import { mapGetters, mapActions } from "vuex";
import { startOfMonth, endOfMonth, format } from "date-fns";

export default {
  components: {
    Modal,
    SecondaryButton,
    MultiSelectSearch,
    MonthPicker,
  },
  props: {
    open: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["close"],
  data() {
    return {
      selectedStores: [],
      storesResults: [],
      selectedMonthRange: [null, null],
    };
  },
  computed: {
    ...mapGetters("store", ["stores", "activeOfflineStores"]),

    filteredStores() {
      return this.activeOfflineStores;
    },

    searchGroups() {
      return [
        {
          key: "stores",
          label: "Points de vente",
          items: this.storesResults,
          getDisplayName: (store) => store.salesChannel,
          isSelected: (storeId) => {
            return this.selectedStores.some((store) => store.id === storeId);
          },
        },
      ];
    },

    initialSelections() {
      return {
        stores: this.selectedStores,
      };
    },

    canExport() {
      return (
        this.selectedStores.length > 0 &&
        this.selectedMonthRange &&
        this.selectedMonthRange.length === 2 &&
        this.selectedMonthRange[0] &&
        this.selectedMonthRange[1]
      );
    },

    startAt() {
      if (!this.selectedMonthRange || !this.selectedMonthRange[0]) return null;
      const date = startOfMonth(this.selectedMonthRange[0]);
      date.setHours(0, 0, 0, 0);
      return format(date, "yyyy-MM-dd'T'HH:mm:ss");
    },

    endAt() {
      if (!this.selectedMonthRange || !this.selectedMonthRange[1]) return null;
      const date = endOfMonth(this.selectedMonthRange[1]);
      date.setHours(23, 59, 59, 999);
      return format(date, "yyyy-MM-dd'T'HH:mm:ss");
    },

    storeIds() {
      return this.selectedStores.map((store) => store.id);
    },
  },
  watch: {
    open(newValue) {
      if (newValue) {
        this.storesResults = [...this.filteredStores];
      } else {
        this.storesResults = [];
        this.selectedStores = [];
        this.selectedMonthRange = [null, null];
      }
    },
  },
  methods: {
    ...mapActions("retributions", ["exportRetributions"]),

    closeModal() {
      this.$emit("close");
    },

    async handleExport() {
      if (!this.canExport) return;

      try {
        const response = await this.exportRetributions({
          storeIds: this.storeIds,
          startAt: this.startAt,
          endAt: this.endAt,
        });

        if (response?.data) {
          download(response.data, "primes-vendeurs.csv");
          this.closeModal();
        }
      } catch (error) {
        console.error("Error exporting retributions:", error);
      }
    },

    handleSearch(keywords) {
      if (!keywords || keywords.trim() === "") {
        this.storesResults = [...this.filteredStores];
      } else {
        this.storesResults = this.filteredStores.filter((store) =>
          store.salesChannel.toLowerCase().includes(keywords.toLowerCase()),
        );
      }
    },

    handleToggleItem({ item, groupKey }) {
      if (groupKey === "stores") {
        const index = this.selectedStores.findIndex((s) => s.id === item.id);
        if (index >= 0) {
          this.selectedStores.splice(index, 1);
        } else {
          this.selectedStores.push(item);
        }
      }
    },

    handleUpdateSelections(selections) {
      this.selectedStores = selections.stores || [];
    },

    selectAllStores() {
      this.selectedStores = [...this.filteredStores];
      this.$nextTick(() => {
        this.handleUpdateSelections({ stores: this.selectedStores });
      });
    },
  },
};
</script>
